import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { DispositivosLayout } from '@/layouts/DispositivosLayout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { TicketsPage } from '@/pages/TicketsPage';
import { UsersPage } from '@/pages/UsersPage';
import { CamerasPage } from '@/pages/CamerasPage';
import { DeviceManagerPage } from '@/pages/DeviceManagerPage';
import { GeofencesPage } from '@/pages/GeofencesPage';
import { AlarmsPage } from '@/pages/AlarmsPage';
import { ArcEventsPage } from '@/pages/ArcEventsPage';
import { ArcDashboardPage } from '@/pages/ArcDashboardPage';
import { ArcHistoryPage } from '@/pages/ArcHistoryPage';
import { CidCodesPage } from '@/pages/CidCodesPage';
import { ChangelogPage } from '@/pages/ChangelogPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { EmergencyPage } from '@/pages/EmergencyPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';
import { AuthConfirmedPage } from '@/pages/AuthConfirmedPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { RolesPage } from '@/pages/RolesPage';
import { AuditPage } from '@/pages/AuditPage';
import { AccountsPage } from '@/pages/AccountsPage';
import { AccountDetailPage } from '@/pages/AccountDetailPage';
import { SiteDetailPage } from '@/pages/SiteDetailPage';
import { DevicesInactivePage } from '@/pages/DevicesInactivePage';
import { InvitesPage } from '@/pages/InvitesPage';
import { JoinRequestsPage } from '@/pages/JoinRequestsPage';
import AsiOnboardingRequestsPage from '@/pages/AsiOnboardingRequestsPage';
import { PlansPage } from '@/pages/PlansPage';
import { RevenuePage } from '@/pages/RevenuePage';
import { CollectorPage } from '@/pages/CollectorPage';
import { PaymentSettingsPage } from '@/pages/PaymentSettingsPage';
import { FelSettingsPage } from '@/pages/FelSettingsPage';
import { BillingProductsPage } from '@/pages/BillingProductsPage';
import { BillingSubscriptionsPage } from '@/pages/BillingSubscriptionsPage';
import { BillingBatchPage } from '@/pages/BillingBatchPage';
import { BillingSettingsPage } from '@/pages/BillingSettingsPage';
import { InvoicesAdminPage } from '@/pages/InvoicesAdminPage';
import { CommunicationsPage } from '@/pages/CommunicationsPage';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { usePermissions } from '@/hooks/usePermissions';
import { ArcRealtimeProvider } from '@/components/ArcRealtimeProvider';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Home of each role (2026-10-29): the monitoring dashboard needs dashboard.view, which the seller and
 * the technician do not have — they land on their own work instead of "unauthorized".
 */
function HomeRoute() {
  const { can } = usePermissions();
  const loading = useAuthStore((st) => st.loading);
  if (loading) return null;
  if (can('dashboard.view')) return <DashboardPage />;
  if (can('alarms.manage') && can('cameras.manage')) return <Navigate to="/devices/panels" replace />;
  if (can('accounts.view')) return <Navigate to="/accounts" replace />;
  return <Navigate to="/unauthorized" replace />;
}

function AuthenticatedApp() {
  const mustChangePassword = useAuthStore((s) => s.profile?.must_change_password);

  // Forced gate: users created with the well-known default password
  // (must_change_password=true on their profile) must set their own before
  // reaching any other screen. Mirrors the same gate in mobile-client/mobile-patrol.
  if (mustChangePassword) {
    return <ChangePasswordModal forced />;
  }

  return (
    <ArcRealtimeProvider soundEnabled>
      <DashboardLayout />
    </ArcRealtimeProvider>
  );
}

export function App() {
  const session = useAuthStore((s) => s.session);
  const loading = useAuthStore((s) => s.loading);
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground text-lg">Cargando...</div>
      </div>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/confirmed" element={<AuthConfirmedPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route
          path="/"
          element={session ? <AuthenticatedApp /> : <Navigate to="/login" />}
        >
          <Route index element={<HomeRoute />} />
          <Route path="emergency" element={<ProtectedRoute require="emergency.view"><EmergencyPage /></ProtectedRoute>} />
          <Route path="tickets" element={<ProtectedRoute require="tickets.view"><TicketsPage /></ProtectedRoute>} />
          <Route path="users" element={<ProtectedRoute require="users.view"><UsersPage /></ProtectedRoute>} />
          <Route path="roles" element={<ProtectedRoute require="users.manage_roles"><RolesPage /></ProtectedRoute>} />
          <Route path="cameras" element={<Navigate to="/devices/cameras" replace />} />
          <Route path="alarms" element={<Navigate to="/devices/panels" replace />} />
          <Route path="devices" element={<ProtectedRoute require="cameras.view"><DispositivosLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="cameras" replace />} />
            <Route path="cameras" element={<CamerasPage />} />
            <Route path="panels" element={<ProtectedRoute require="alarms.view"><AlarmsPage /></ProtectedRoute>} />
            <Route path="registro" element={<ProtectedRoute require="cameras.manage"><DeviceManagerPage /></ProtectedRoute>} />
            <Route path="inactivos" element={<DevicesInactivePage />} />
          </Route>
          <Route path="arc/events" element={<ProtectedRoute require="arc.events.view"><ArcEventsPage /></ProtectedRoute>} />
          <Route path="arc/dashboard" element={<ProtectedRoute require="arc.events.view"><ArcDashboardPage /></ProtectedRoute>} />
          <Route path="arc/historial" element={<Navigate to="/arc/history" replace />} />
          <Route path="arc/history" element={<ProtectedRoute require="arc.events.view"><ArcHistoryPage /></ProtectedRoute>} />
          <Route path="arc/accounts" element={<Navigate to="/arc/events" replace />} />
          <Route path="arc/cid-codes" element={<ProtectedRoute require="arc.cid_codes.view"><CidCodesPage /></ProtectedRoute>} />
          <Route path="accounts" element={<ProtectedRoute require="accounts.view"><AccountsPage /></ProtectedRoute>} />
          <Route path="accounts/:id" element={<ProtectedRoute require="accounts.view"><AccountDetailPage /></ProtectedRoute>} />
          <Route path="accounts/:id/invites" element={<ProtectedRoute require="accounts.view"><InvitesPage /></ProtectedRoute>} />
          <Route path="join-requests" element={<ProtectedRoute require="accounts.view"><JoinRequestsPage /></ProtectedRoute>} />
          <Route path="onboarding-requests" element={<ProtectedRoute require="accounts.view"><AsiOnboardingRequestsPage /></ProtectedRoute>} />
          <Route path="admin/plans" element={<ProtectedRoute require="plans.view"><PlansPage /></ProtectedRoute>} />
          <Route path="admin/revenue" element={<ProtectedRoute require="revenue.view"><RevenuePage /></ProtectedRoute>} />
          <Route path="admin/collections" element={<ProtectedRoute require="collections.view"><CollectorPage /></ProtectedRoute>} />
          <Route path="admin/payments" element={<ProtectedRoute require="plans.manage"><PaymentSettingsPage /></ProtectedRoute>} />
          <Route path="admin/fel" element={<ProtectedRoute require="billing.manage"><FelSettingsPage /></ProtectedRoute>} />
          <Route path="admin/billing/products" element={<ProtectedRoute require="billing.view"><BillingProductsPage /></ProtectedRoute>} />
          <Route path="admin/billing/subscriptions" element={<ProtectedRoute require="billing.view"><BillingSubscriptionsPage /></ProtectedRoute>} />
          <Route path="admin/billing/batch" element={<ProtectedRoute require="billing.manage"><BillingBatchPage /></ProtectedRoute>} />
          <Route path="admin/billing/settings" element={<ProtectedRoute require="billing.manage"><BillingSettingsPage /></ProtectedRoute>} />
          <Route path="admin/billing/invoices" element={<ProtectedRoute require="collections.view"><InvoicesAdminPage /></ProtectedRoute>} />
          <Route path="admin/communications" element={<ProtectedRoute require="communications.view"><CommunicationsPage /></ProtectedRoute>} />
          <Route path="sites/:id" element={<ProtectedRoute require="accounts.view"><SiteDetailPage /></ProtectedRoute>} />
          <Route path="geofences" element={<ProtectedRoute require="geofences.view"><GeofencesPage /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute require="settings.manage"><SettingsPage /></ProtectedRoute>} />
          <Route path="settings/changelog" element={<ProtectedRoute require="changelog.view"><ChangelogPage /></ProtectedRoute>} />
          <Route path="audit" element={<ProtectedRoute require="audit.view"><AuditPage /></ProtectedRoute>} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
